import { useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { MobileDrawer } from 'components/UI/V2/MobileDrawer/MobileDrawer';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import BrandColorPicker from './BrandColorPicker';
import styles from './HexColorField.module.scss';

export const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;

interface HexColorFieldProps {
    title: string;
    description: string;
    value: string;
    fallbackColor: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    onReset: () => void;
}

const HexColorField: FunctionComponent<HexColorFieldProps> = props => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const isValid = HEX_COLOR_REGEX.test(props.value);
    const swatchColor = isValid ? props.value : props.fallbackColor;

    useEffect(() => {
        if (!pickerOpen || isMobile) return;
        const clickOutsideHandler = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', clickOutsideHandler);
        return () => document.removeEventListener('mousedown', clickOutsideHandler);
    }, [pickerOpen, isMobile]);

    const changeHandler = (rawValue: string) => {
        const value = rawValue.trim().toUpperCase();
        props.onChange(value === '' || value.startsWith('#') ? value : `#${value}`);
    };

    return (
        <div className={styles.container}>
            <div
                ref={wrapperRef}
                className={styles.fieldWrapper}
            >
                <p className={styles.title}>{props.title}</p>
                <p className={styles.description}>{props.description}</p>
                <div className={styles.row}>
                    <button
                        className={styles.swatch}
                        style={{ backgroundColor: swatchColor }}
                        aria-label={`Pick ${props.title.toLowerCase()}`}
                        disabled={props.disabled}
                        onClick={() => setPickerOpen(open => !open)}
                    />
                    <div className={styles.inputWrapper}>
                        <div
                            className={styles.dot}
                            style={{ backgroundColor: swatchColor }}
                        />
                        <input
                            className={styles.input}
                            value={props.value}
                            maxLength={7}
                            disabled={props.disabled}
                            onChange={e => changeHandler(e.target.value)}
                        />
                    </div>
                    <button
                        className={styles.reset}
                        disabled={props.disabled}
                        onClick={props.onReset}
                    >
                        Reset
                    </button>
                </div>
                {pickerOpen &&
                    (isMobile ? (
                        <MobileDrawer onClose={() => setPickerOpen(false)}>
                            <BrandColorPicker
                                color={swatchColor}
                                variant="drawer"
                                onChange={props.onChange}
                                onClose={() => setPickerOpen(false)}
                            />
                        </MobileDrawer>
                    ) : (
                        <BrandColorPicker
                            color={swatchColor}
                            onChange={props.onChange}
                            onClose={() => setPickerOpen(false)}
                        />
                    ))}
            </div>
            <p className={clsx(styles.helper, { [styles.helperError]: !isValid })}>Color must be in HEX format</p>
        </div>
    );
};

export default HexColorField;
